/**
 * Plan and billing: the restaurant's side.
 *
 * No payment provider is integrated. A restaurant asks for a plan here; the
 * operator approves it (which invoices it) outside the app. So nothing in this
 * file changes a plan — a change request is a request, and the page says so.
 *
 * Money here is in the platform's currency (`BillingAccount.currency`), not
 * the restaurant's: pass it to `formatMoney` explicitly.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import type { ApiError } from './client';

import { useApi } from './provider';

export type BillingCycle = 'month' | 'year';

export interface PlanSummary {
  key: string;
  name: string;
  tier: number;
  /** Null: no list price; agreed with the operator. */
  price_month: string | null;
  price_year: string | null;
}

export interface PendingPlanChange {
  id: string;
  plan: PlanSummary;
  cycle: BillingCycle;
  note: string;
  requested_at: string;
}

/** A free trial the platform gave. While it runs, `plan` is the trial's plan. */
export interface BillingTrial {
  plan: PlanSummary;
  /** What the restaurant goes back to when it ends. */
  previous_plan: PlanSummary;
  previous_cycle: BillingCycle;
  starts_at: string;
  ends_at: string;
  /** Whole days to go, a part day counted as one. */
  days_left: number;
}

export interface BillingAccount {
  plan: PlanSummary;
  /**
   * "active"; "trialing" during a free trial (nothing is stopped); or
   * "past_due" when suspended for an unpaid invoice.
   */
  status: string;
  cycle: BillingCycle;
  current_period_end: string | null;
  currency: string;
  /** How to pay, as the operator wrote it. May span lines. */
  pay_to: string;
  pending: PendingPlanChange | null;
  last_decision: { status: 'approved' | 'declined'; note: string; decided_at: string } | null;
  trial: BillingTrial | null;
}

export interface PlanOption {
  plan: PlanSummary;
  /** Built features only. */
  features: string[];
  /** Null: unlimited. */
  limits: Record<string, string | null>;
}

export interface PlanComparison {
  current: string;
  options: PlanOption[];
  usage: Record<string, number>;
  feature_names: Record<string, string>;
  limit_names: Record<string, string>;
}

export interface BillingInvoice {
  id: string;
  number: string;
  description: string;
  period_start: string | null;
  period_end: string | null;
  amount: string;
  currency: string;
  status: 'open' | 'paid' | 'void';
  issued_at: string;
  due_on: string;
  paid_at: string | null;
  payment_reference: string;
}

export function useBillingAccount() {
  const api = useApi();
  return useQuery({
    queryKey: ['billing', 'account'],
    queryFn: ({ signal }) => api.get<BillingAccount>('/v1/billing/account', signal),
  });
}

export function usePlanComparison() {
  const api = useApi();
  return useQuery({
    queryKey: ['billing', 'compare'],
    queryFn: ({ signal }) => api.get<PlanComparison>('/v1/billing/plans/compare', signal),
  });
}

export function useBillingInvoices() {
  const api = useApi();
  return useQuery({
    queryKey: ['billing', 'invoices'],
    queryFn: ({ signal }) => api.get<BillingInvoice[]>('/v1/billing/invoices', signal),
  });
}

/** 409 when a change is already waiting, or when it would change nothing. */
export function useRequestPlanChange() {
  const api = useApi();
  const queryClient = useQueryClient();
  return useMutation<
    PendingPlanChange,
    ApiError,
    { plan: string; cycle: BillingCycle; note: string }
  >({
    mutationFn: (input) => api.post<PendingPlanChange>('/v1/billing/change-requests', input),
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: ['billing'] });
    },
  });
}

export function useWithdrawPlanChange() {
  const api = useApi();
  const queryClient = useQueryClient();
  return useMutation<void, ApiError, { id: string }>({
    mutationFn: (input) =>
      api.request<void>(`/v1/billing/change-requests/${input.id}`, { method: 'DELETE' }),
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: ['billing'] });
    },
  });
}
